import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import useSmoothedText from '~tread-agent/parts/useSmoothedText';

interface TextPartProps {
  // While true, reveals text gradually and shows a caret, so the backend's coalesced
  // ~60-char delta flushes read as smooth streaming rather than jumps.
  streaming?: boolean;
  text: string;
}

// Assistant text rendered as GitHub-flavored markdown, styled to match the app.
// react-markdown does not render raw HTML, so model output can't inject markup.
export default function TextPart({ streaming = false, text }: TextPartProps) {
  const theme = useTheme();
  const shown = useSmoothedText(text, streaming);

  return (
    <Box
      sx={{
        color: theme.brandV2.colors.treadGray1,
        fontSize: '13px',
        lineHeight: 1.55,
        wordBreak: 'break-word',
        '& p:first-of-type': { mt: 0 },
        '& p:last-of-type': { mb: 0 },
        '& p': { my: 1 },
        '& ul, & ol': { my: 1, pl: 2.5 },
        '& li': { mb: 0.5 },
        '& li > p': { my: 0 },
        '& strong': { fontWeight: 600 },
        '& a': { color: theme.brandV2.colors.treadBlue, textDecoration: 'underline' },
        '& a[data-external]': {
          backgroundColor: theme.brandV2.colors.treadGray8,
          borderRadius: '4px',
          boxDecorationBreak: 'clone',
          px: 0.75,
          py: 0.25,
          textDecoration: 'none',
          '&:hover': { backgroundColor: theme.brandV2.colors.treadGray7 },
          '& svg': { fontSize: 14, mr: 0.25, verticalAlign: '-2px' },
        },
        '& code': {
          backgroundColor: theme.brandV2.colors.treadGray8,
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
          px: 0.5,
        },
        '& pre': {
          backgroundColor: theme.brandV2.colors.treadGray8,
          borderRadius: '4px',
          overflowX: 'auto',
          p: 1,
        },
        '& pre code': { backgroundColor: 'transparent', px: 0 },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontSize: '14px',
          fontWeight: 600,
          my: 0.5,
        },
        '& table': { borderCollapse: 'collapse', width: '100%' },
        '& th, & td': {
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          px: 0.5,
          py: 0.25,
          textAlign: 'left',
        },
      }}
    >
      <Markdown
        components={{
          a: ({ children, href }) => {
            let linkOrigin: string | null = null;
            try {
              linkOrigin = href ? new URL(href, window.location.href).origin : null;
            } catch {
              // A destination that is not a valid URL is shown as plain text instead of crashing the chat.
              return <>{children}</>;
            }
            const isExternal =
              linkOrigin !== null && linkOrigin !== window.location.origin;

            return (
              <a
                data-external={isExternal || undefined}
                href={href}
                rel="noopener noreferrer"
                target="_blank"
              >
                {isExternal && <ArrowOutwardIcon sx={{ fontSize: 14 }} />}
                {children}
              </a>
            );
          },
        }}
        remarkPlugins={[remarkGfm]}
      >
        {streaming ? `${shown}▍` : shown}
      </Markdown>
    </Box>
  );
}
